import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import { ListLayout } from "@/components/custom/list-layout/list-layout";

const MEMBER_ROWS = ["a", "b", "c", "d"];
const DETAIL_ROWS = ["projects", "members", "created"];

/**
 * Loading state for the org members section. Mirrors `OrgMembersCard`: the
 * Users-icon header and a table with the Name / Email / Role / Projects columns.
 */
export function OrgMembersCardSkeleton() {
  return (
    <section className="rounded-xl border bg-card/50 p-4">
      <div className="mb-4 flex items-start gap-3">
        <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
        <div className="space-y-1.5">
          <Skeleton className="h-5 w-28" />
          <Skeleton className="h-4 w-40" />
        </div>
      </div>
      <div className="overflow-hidden rounded-lg border">
        <div className="bg-muted/50 px-4 py-3">
          <div className="flex gap-8">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-16" />
          </div>
        </div>
        <div className="divide-y">
          {MEMBER_ROWS.map((row) => (
            <div className="flex items-center gap-4 px-4 py-3" key={row}>
              <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-44" />
              <Skeleton className="h-6 w-24 rounded-md" />
              <Skeleton className="ml-auto h-5 w-20 rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Full loading state for the Organization overview page: the static header,
 * the organization detail card (identity + Projects/Members/Created), the
 * separate organization ID strip, and the members section.
 */
export function OrganizationPageSkeleton() {
  return (
    <ListLayout
      description="A summary of your organization and the people in it."
      title="Organization"
    >
      <section className="rounded-xl border bg-card/50">
        <div className="flex flex-col gap-5 p-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-5 w-44" />
              <Skeleton className="h-4 w-64 max-w-full" />
            </div>
          </div>
          <div className="grid min-w-0 grid-cols-3 gap-x-4 gap-y-3 sm:gap-x-8 lg:min-w-[24rem]">
            {DETAIL_ROWS.map((row) => (
              <div className="space-y-1.5" key={row}>
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-4 w-12" />
              </div>
            ))}
          </div>
        </div>
        <div className="border-t px-4 py-3">
          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
            <Skeleton className="h-3.5 w-24 shrink-0" />
            <div className="flex w-full min-w-0 items-center gap-1.5 sm:w-auto">
              <Skeleton className="h-7 w-72 max-w-full rounded-md" />
              <Skeleton className="h-8 w-8 shrink-0 rounded-md" />
            </div>
          </div>
        </div>
      </section>

      <OrgMembersCardSkeleton />
    </ListLayout>
  );
}
